const API_BASE_URL = 'https://openapi.nocodebackend.com';
const API_KEY = '912a1e02debc798a039f0996262ed8ebe20b46e7f3da62cead15797309ad';
const INSTANCE = '53658_db_ctd_v4';

class ApiService {
  constructor() {
    this.headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${API_KEY}`
    };
  }

  async request(endpoint, method = 'GET', data = null) {
    // Determine the correct separator for the Instance parameter
    const hasQuery = endpoint.includes('?');
    const separator = hasQuery ? '&' : '?';
    const url = `${API_BASE_URL}${endpoint}${separator}Instance=${INSTANCE}`;
    
    const config = {
      method,
      headers: this.headers,
    };

    if (data && method !== 'GET') {
      config.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, config);
      
      // Handle 204 No Content specifically
      if (response.status === 204) {
        return null; 
      }

      const text = await response.text();
      let result;

      try {
        // Try to parse as JSON if there is text
        result = text ? JSON.parse(text) : {};
      } catch (e) {
        // If parsing fails, treat as text response or empty success
        // This is crucial for DELETE requests that might return "OK" or empty body but not JSON
        result = { message: text || 'Operation successful' };
      }
      
      if (!response.ok) {
        throw new Error(result.message || `API request failed with status ${response.status}`);
      }
      
      return result;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Helper to merge default limit with filters
  _getParams(filters) {
    // Default limit to 1000 to ensure we fetch all records by default
    const mergedFilters = { limit: 1000, ...filters };
    return new URLSearchParams(mergedFilters).toString();
  }

  // User methods
  async createUser(userData) {
    return this.request('/create/user', 'POST', userData);
  }

  async getUsers(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/user${params ? `?${params}` : ''}`);
  }

  async getUserById(id) {
    return this.request(`/read/user/${id}`);
  }

  async updateUser(id, userData) {
    return this.request(`/update/user/${id}`, 'PUT', userData);
  }

  async deleteUser(id) {
    return this.request(`/delete/user/${id}`, 'DELETE');
  }

  async searchUsers(searchData) {
    return this.request('/search/user', 'POST', searchData);
  }

  // Team methods
  async createTeam(teamData) {
    return this.request('/create/team', 'POST', teamData);
  }

  async getTeams(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/team${params ? `?${params}` : ''}`);
  }

  async getTeamById(id) {
    return this.request(`/read/team/${id}`);
  }

  async updateTeam(id, teamData) {
    return this.request(`/update/team/${id}`, 'PUT', teamData);
  }

  async deleteTeam(id) {
    return this.request(`/delete/team/${id}`, 'DELETE');
  }

  async searchTeams(searchData) {
    return this.request('/search/team', 'POST', searchData);
  }

  // User-Team relationship methods
  async createUserTeamRelation(relationData) {
    return this.request('/create/user_has_team', 'POST', relationData);
  }

  async getUserTeamRelations(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/user_has_team${params ? `?${params}` : ''}`);
  }

  async getUserTeamRelationById(id) {
    return this.request(`/read/user_has_team/${id}`);
  }

  async updateUserTeamRelation(id, relationData) {
    return this.request(`/update/user_has_team/${id}`, 'PUT', relationData);
  }

  async deleteUserTeamRelation(id) {
    return this.request(`/delete/user_has_team/${id}`, 'DELETE');
  }

  async searchUserTeamRelations(searchData) {
    return this.request('/search/user_has_team', 'POST', searchData);
  }

  // Element methods
  async createElement(elementData) {
    return this.request('/create/element', 'POST', elementData);
  }

  async getElements(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/element${params ? `?${params}` : ''}`);
  }

  async getElementById(id) {
    return this.request(`/read/element/${id}`);
  }

  async updateElement(id, elementData) {
    return this.request(`/update/element/${id}`, 'PUT', elementData);
  }

  async deleteElement(id) {
    return this.request(`/delete/element/${id}`, 'DELETE');
  }

  async searchElements(searchData) {
    return this.request('/search/element', 'POST', searchData);
  }

  // Element Connection methods (element_has_connection)
  async createElementConnection(connectionData) {
    return this.request('/create/element_has_connection', 'POST', connectionData);
  }

  async getElementConnections(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/element_has_connection${params ? `?${params}` : ''}`);
  }

  async getElementConnectionById(id) {
    return this.request(`/read/element_has_connection/${id}`);
  }

  async updateElementConnection(id, connectionData) {
    return this.request(`/update/element_has_connection/${id}`, 'PUT', connectionData);
  }

  async deleteElementConnection(id) {
    return this.request(`/delete/element_has_connection/${id}`, 'DELETE');
  }

  async searchElementConnections(searchData) {
    return this.request('/search/element_has_connection', 'POST', searchData);
  }

  // Question methods
  async createQuestion(questionData) {
    return this.request('/create/question', 'POST', questionData);
  }

  async getQuestions(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/question${params ? `?${params}` : ''}`);
  }

  async getQuestionById(id) {
    return this.request(`/read/question/${id}`);
  }

  async updateQuestion(id, questionData) {
    return this.request(`/update/question/${id}`, 'PUT', questionData);
  }

  async deleteQuestion(id) {
    return this.request(`/delete/question/${id}`, 'DELETE');
  }

  async searchQuestions(searchData) {
    return this.request('/search/question', 'POST', searchData);
  }

  // Evaluation methods
  async createEvaluation(evaluationData) {
    return this.request('/create/question_has_evaluation', 'POST', evaluationData);
  }

  async getEvaluations(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/question_has_evaluation${params ? `?${params}` : ''}`);
  }

  async getEvaluationById(id) {
    return this.request(`/read/question_has_evaluation/${id}`);
  }

  async updateEvaluation(id, evaluationData) {
    return this.request(`/update/question_has_evaluation/${id}`, 'PUT', evaluationData);
  }

  async deleteEvaluation(id) {
    return this.request(`/delete/question_has_evaluation/${id}`, 'DELETE');
  }

  async searchEvaluations(searchData) {
    return this.request('/search/question_has_evaluation', 'POST', searchData);
  }

  // Team uses element methods
  async createTeamUsesElement(data) {
    return this.request('/create/team_uses_element', 'POST', data);
  }

  async getTeamUsesElement(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/team_uses_element${params ? `?${params}` : ''}`);
  }

  async getTeamUsesElementById(id) {
    return this.request(`/read/team_uses_element/${id}`);
  }

  async updateTeamUsesElement(id, data) {
    return this.request(`/update/team_uses_element/${id}`, 'PUT', data);
  }

  async deleteTeamUsesElement(id) {
    return this.request(`/delete/team_uses_element/${id}`, 'DELETE');
  }

  async searchTeamUsesElements(searchData) {
    return this.request('/search/team_uses_element', 'POST', searchData);
  }

  // Recommendation methods
  async createRecommendation(recommendationData) {
    return this.request('/create/recommendation', 'POST', recommendationData);
  }

  async getRecommendations(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/recommendation${params ? `?${params}` : ''}`);
  }

  async getRecommendationById(id) {
    return this.request(`/read/recommendation/${id}`);
  }

  async updateRecommendation(id, recommendationData) {
    return this.request(`/update/recommendation/${id}`, 'PUT', recommendationData);
  }

  async deleteRecommendation(id) {
    return this.request(`/delete/recommendation/${id}`, 'DELETE');
  }

  async searchRecommendations(searchData) {
    return this.request('/search/recommendation', 'POST', searchData);
  }

  // Question-Recommendation relationship methods
  async createQuestionRecommendationRelation(relationData) {
    return this.request('/create/question_has_recommendation', 'POST', relationData);
  }

  async getQuestionRecommendationRelations(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/question_has_recommendation${params ? `?${params}` : ''}`);
  }

  async getQuestionRecommendationRelationById(id) {
    return this.request(`/read/question_has_recommendation/${id}`);
  }

  async updateQuestionRecommendationRelation(id, relationData) {
    return this.request(`/update/question_has_recommendation/${id}`, 'PUT', relationData);
  }

  async deleteQuestionRecommendationRelation(id) {
    return this.request(`/delete/question_has_recommendation/${id}`, 'DELETE');
  }

  async searchQuestionRecommendationRelations(searchData) {
    return this.request('/search/question_has_recommendation', 'POST', searchData);
  }

  // Team used recommendation methods
  async createTeamUsedRecommendation(data) {
    return this.request('/create/team_used_recommendation', 'POST', data);
  }

  async getTeamUsedRecommendations(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/team_used_recommendation${params ? `?${params}` : ''}`);
  }

  async getTeamUsedRecommendationById(id) {
    return this.request(`/read/team_used_recommendation/${id}`);
  }

  async updateTeamUsedRecommendation(id, data) {
    return this.request(`/update/team_used_recommendation/${id}`, 'PUT', data);
  }

  async deleteTeamUsedRecommendation(id) {
    return this.request(`/delete/team_used_recommendation/${id}`, 'DELETE');
  }

  async searchTeamUsedRecommendations(searchData) {
    return this.request('/search/team_used_recommendation', 'POST', searchData);
  }

  // Element-Recommendation relationship methods
  async createElementRecommendationRelation(relationData) {
    return this.request('/create/element_has_recommendation', 'POST', relationData);
  }

  async getElementRecommendationRelations(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/element_has_recommendation${params ? `?${params}` : ''}`);
  }

  async getElementRecommendationRelationById(id) {
    return this.request(`/read/element_has_recommendation/${id}`);
  }

  async updateElementRecommendationRelation(id, relationData) {
    return this.request(`/update/element_has_recommendation/${id}`, 'PUT', relationData);
  }

  async deleteElementRecommendationRelation(id) {
    return this.request(`/delete/element_has_recommendation/${id}`, 'DELETE');
  }

  async searchElementRecommendationRelations(searchData) {
    return this.request('/search/element_has_recommendation', 'POST', searchData);
  }

  // Cluster methods
  async createCluster(clusterData) {
    return this.request('/create/cluster', 'POST', clusterData);
  }

  async getClusters(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/cluster${params ? `?${params}` : ''}`);
  }

  async getClusterById(id) {
    return this.request(`/read/cluster/${id}`);
  }

  async updateCluster(id, clusterData) {
    return this.request(`/update/cluster/${id}`, 'PUT', clusterData);
  }

  async deleteCluster(id) {
    return this.request(`/delete/cluster/${id}`, 'DELETE');
  }

  async searchClusters(searchData) {
    return this.request('/search/cluster', 'POST', searchData);
  }

  // Cluster-Element relationship methods
  async createClusterElementRelation(relationData) {
    return this.request('/create/cluster_has_element', 'POST', relationData);
  }

  async getClusterElementRelations(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/cluster_has_element${params ? `?${params}` : ''}`);
  }

  async getClusterElementRelationById(id) {
    return this.request(`/read/cluster_has_element/${id}`);
  }

  async updateClusterElementRelation(id, relationData) {
    return this.request(`/update/cluster_has_element/${id}`, 'PUT', relationData);
  }

  async deleteClusterElementRelation(id) {
    return this.request(`/delete/cluster_has_element/${id}`, 'DELETE');
  }

  async searchClusterElementRelations(searchData) {
    return this.request('/search/cluster_has_element', 'POST', searchData);
  }

  // Release Notes methods
  async createReleaseNote(noteData) {
    return this.request('/create/release_notes', 'POST', noteData);
  }

  async getReleaseNotes(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/release_notes${params ? `?${params}` : ''}`);
  }

  async getReleaseNoteById(id) {
    return this.request(`/read/release_notes/${id}`);
  }

  async updateReleaseNote(id, noteData) {
    return this.request(`/update/release_notes/${id}`, 'PUT', noteData);
  }

  async deleteReleaseNote(id) {
    return this.request(`/delete/release_notes/${id}`, 'DELETE');
  }

  async searchReleaseNotes(searchData) {
    return this.request('/search/release_notes', 'POST', searchData);
  }

  // Feature Request methods
  async createFeatureRequest(data) {
    return this.request('/create/feature_request', 'POST', data);
  }

  async getFeatureRequests(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/feature_request${params ? `?${params}` : ''}`);
  }

  async getFeatureRequestById(id) {
    return this.request(`/read/feature_request/${id}`);
  }

  async updateFeatureRequest(id, data) {
    return this.request(`/update/feature_request/${id}`, 'PUT', data);
  }

  async deleteFeatureRequest(id) {
    return this.request(`/delete/feature_request/${id}`, 'DELETE');
  }

  async searchFeatureRequests(searchData) {
    return this.request('/search/feature_request', 'POST', searchData);
  }

  // Bug Report methods
  async createBugReport(data) {
    return this.request('/create/bug_report', 'POST', data);
  }

  async getBugReports(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/bug_report${params ? `?${params}` : ''}`);
  }

  async getBugReportById(id) {
    return this.request(`/read/bug_report/${id}`);
  }

  async updateBugReport(id, data) {
    return this.request(`/update/bug_report/${id}`, 'PUT', data);
  }

  async deleteBugReport(id) {
    return this.request(`/delete/bug_report/${id}`, 'DELETE');
  }

  async searchBugReports(searchData) {
    return this.request('/search/bug_report', 'POST', searchData);
  }

  // Feedback Questions methods
  async createFeedbackQuestion(data) {
    return this.request('/create/feedback_question', 'POST', data);
  }

  async getFeedbackQuestions(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/feedback_question${params ? `?${params}` : ''}`);
  }

  async getFeedbackQuestionById(id) {
    return this.request(`/read/feedback_question/${id}`);
  }

  async updateFeedbackQuestion(id, data) {
    return this.request(`/update/feedback_question/${id}`, 'PUT', data);
  }

  async deleteFeedbackQuestion(id) {
    return this.request(`/delete/feedback_question/${id}`, 'DELETE');
  }

  async searchFeedbackQuestions(searchData) {
    return this.request('/search/feedback_question', 'POST', searchData);
  }

  // User Feedback methods (user_has_feedback)
  async createUserFeedback(data) {
    return this.request('/create/user_has_feedback', 'POST', data);
  }

  async getUserFeedbacks(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/user_has_feedback${params ? `?${params}` : ''}`);
  }

  async getUserFeedbackById(id) {
    return this.request(`/read/user_has_feedback/${id}`);
  }

  async updateUserFeedback(id, data) {
    return this.request(`/update/user_has_feedback/${id}`, 'PUT', data);
  }

  async deleteUserFeedback(id) {
    return this.request(`/delete/user_has_feedback/${id}`, 'DELETE');
  }

  async searchUserFeedbacks(searchData) {
    return this.request('/search/user_has_feedback', 'POST', searchData);
  }

  // Feedback Statement methods
  async createFeedbackStatement(data) {
    return this.request('/create/feedback_statement', 'POST', data);
  }

  async getFeedbackStatements(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/feedback_statement${params ? `?${params}` : ''}`);
  }

  async getFeedbackStatementById(id) {
    return this.request(`/read/feedback_statement/${id}`);
  }

  async updateFeedbackStatement(id, data) {
    return this.request(`/update/feedback_statement/${id}`, 'PUT', data);
  }

  async deleteFeedbackStatement(id) {
    return this.request(`/delete/feedback_statement/${id}`, 'DELETE');
  }

  async searchFeedbackStatements(searchData) {
    return this.request('/search/feedback_statement', 'POST', searchData);
  }

  // Announcement methods
  async createAnnouncement(data) {
    return this.request('/create/announcement', 'POST', data);
  }

  async getAnnouncements(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/announcement${params ? `?${params}` : ''}`);
  }

  async getAnnouncementById(id) {
    return this.request(`/read/announcement/${id}`);
  }

  async updateAnnouncement(id, data) {
    return this.request(`/update/announcement/${id}`, 'PUT', data);
  }

  async deleteAnnouncement(id) {
    return this.request(`/delete/announcement/${id}`, 'DELETE');
  }

  async searchAnnouncements(searchData) {
    return this.request('/search/announcement', 'POST', searchData);
  }

  // Recommendation Status Change methods
  async createRecommendationStatusChange(data) {
    return this.request('/create/recommendation_status_change', 'POST', data);
  }

  async getRecommendationStatusChanges(filters = {}) {
    const params = this._getParams(filters);
    return this.request(`/read/recommendation_status_change${params ? `?${params}` : ''}`);
  }

  async getRecommendationStatusChangeById(id) {
    return this.request(`/read/recommendation_status_change/${id}`);
  }

  async updateRecommendationStatusChange(id, data) {
    return this.request(`/update/recommendation_status_change/${id}`, 'PUT', data);
  }

  async deleteRecommendationStatusChange(id) {
    return this.request(`/delete/recommendation_status_change/${id}`, 'DELETE');
  }

  async searchRecommendationStatusChanges(searchData) {
    return this.request('/search/recommendation_status_change', 'POST', searchData);
  }
}

export default new ApiService();